import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Pill } from '../../../../../../shared/sso-workspace/pill'
import { OrgLinkPanel } from '../../../../../../shared/sso-workspace/org-link-panel'
import { ConfirmDialog } from '../../../../../../shared/sso-workspace/confirm-dialog'
import { useApiFormProviderOrganization } from '../../../../../../api-new/state/auth'
import { providerOrganizationRuleLabel } from '../provider-organization-rule'
import { OrgRuleEditor } from './routing-rule-editor'
import { FormNewOrganizationSelect } from './fields'
import { WorkspaceDomains } from './section-domains'
import { JoinPolicyEditor, JoinPolicyPill, joinPolicyLabel, linkJoinPolicy } from '../../../../../../shared/sso-workspace/join-policy'
import { JoinCodesPanel } from '../../../../../../shared/sso-workspace/join-codes-panel'
import { FleetGrid, useGridList } from '../../../../../../shared/sso-workspace/fleet-grid'
import { TableAgClient } from '../../../../../../shared/table-builder/table-ag-client'
import { ColumnFormat, ColumnMenu, ColumnText } from '../../../../../../shared/table-builder/table-columns'
import { Menu, MenuItem } from '../../../../../../shared/menu/menu'
import { IconAdd, IconDelete, IconEdit, IconLoginPin, IconOrganization, IconUsers } from '../../../../../../shared/icon/icon'
import { useErrorLine } from '../../../../../../shared/error/use-error-line'

export const STATUS_MS = 3000
const HIGHLIGHT_MS = 1800

// Where a row-menu button sits in the grid (its row and its place among the
// row's buttons), and the button in that spot now.
const rowButtonSpot = button => {
  const row = button && button.closest && button.closest('.ag-row')
  if (!row) return null
  return { rowIndex: row.getAttribute('row-index'), position: Array.from(row.querySelectorAll('button')).indexOf(button) }
}

const findRowButton = (root, spot) => {
  if (!root || !spot) return null
  const row = root.querySelector(`.ag-pinned-right-cols-container .ag-row[row-index="${spot.rowIndex}"]`)
  return row ? row.querySelectorAll('button')[spot.position] || null : null
}

// Who joins a linked organization, in words: the grid's cell value, so it
// sorts, filters and exports as the row reads.
const joinsWhenText = l => {
  const ruled = Boolean(l.matchData && l.matchData.claim)
  const policy = linkJoinPolicy(l)
  if (policy === 'everyone') return ruled ? 'Every sign-in (the rule is not used)' : 'Every sign-in (no rule: catch-all)'
  if (policy === 'rule') return providerOrganizationRuleLabel(l.matchData)
  return [joinPolicyLabel(policy), ruled ? providerOrganizationRuleLabel(l.matchData) : ''].filter(Boolean).join(' · ')
}

const joinsWhenCell = row => {
  const l = row.data
  if (!l) return ''
  const ruled = Boolean(l.matchData && l.matchData.claim)
  const policy = linkJoinPolicy(l)
  if (policy === 'everyone') {
    return <span className="d-inline-flex flex-wrap align-items-center gap-2"><Pill tone="warn">Every sign-in</Pill><span className="sw-hint">{ruled ? 'the rule is not used' : 'no rule: catch-all'}</span></span>
  }
  if (policy === 'rule') return <span>{providerOrganizationRuleLabel(l.matchData)}</span>
  return (
    <span className="d-inline-flex flex-wrap align-items-center gap-2">
      <JoinPolicyPill policy={policy} />
      {ruled && <span className="sw-hint">{providerOrganizationRuleLabel(l.matchData)}</span>}
    </span>
  )
}

const serverMessage = (exp, fallback) => (exp && exp.data && exp.data.message) || fallback

// Who is sent to this connection (its email domains) and which organization
// they join (the provider_organizations links and their claim rules).
// Linking, unlinking and rule edits all happen here, in panels under the
// table, and save through /provider-organization on their own (never with
// the page's Save). The links are the shared AG Grid; the open editor or
// panel sits under it.
//
// claims: the connection's claim names (the Claims & mapping chips' list),
// offered by the rule editor. canUnlink: the old list's delete permission.
// connectionName / providerActive / ownerOrganizationID and the two
// *Changed callbacks feed the email domains' bind and unbind (audit B1).
export const SectionRouting = ({ mode, providerID, domains, links, claims, onLinksChanged, highlightRule, canEdit = true, canUnlink = canEdit,
  connectionName, providerActive, providerPublic, ownerOrganizationID, onDomainsChanged, onProviderChanged }) => {
  const api = useApiFormProviderOrganization()
  const staff = mode !== 'customer'
  const [linkOpen, setLinkOpen] = useState(false)
  const [linkingID, setLinkingID] = useState(null)
  const [linkError, linkFailure, setLinkError] = useErrorLine()
  const [editing, setEditing] = useState(null) // the link whose rule is open
  const [panel, setPanel] = useState(null) // { kind: 'policy' | 'codes', organizationID }
  const [unlinking, setUnlinking] = useState(null) // the link being confirmed
  const [unlinkBusy, setUnlinkBusy] = useState(false)
  const [unlinkError, unlinkFailure, setUnlinkError] = useErrorLine()
  const [flashID, setFlashID] = useState(null)
  const [status, setStatus] = useState('')
  const linkButton = useRef(null)
  // Where focus goes back to when a panel or the dialog closes: the button
  // that opened it.
  const opener = useRef(null)
  const openerSpot = useRef(null)
  const gridBox = useRef(null)
  const gridApi = useRef(null)
  const tableRef = useRef()
  const setOpener = button => { opener.current = button; openerSpot.current = rowButtonSpot(button) }
  const timers = useRef({})

  useEffect(() => () => Object.values(timers.current).forEach(clearTimeout), [])

  const later = (key, fn, ms) => {
    clearTimeout(timers.current[key])
    timers.current[key] = setTimeout(fn, ms)
  }
  const announce = text => { setStatus(text); later('status', () => setStatus(''), STATUS_MS) }
  const flash = id => { setFlashID(id); later('flash', () => setFlashID(null), HIGHLIGHT_MS) }
  const refocus = () => {
    const el = opener.current
    const spot = openerSpot.current
    opener.current = null
    openerSpot.current = null
    // After the panel has unmounted, so its own focus handling is done. The
    // grid re-draws its rows when the links reload, replacing the button
    // that opened the panel: then focus goes to the button in the same place
    // of the same row.
    setTimeout(() => {
      if (el && el.isConnected) { el.focus(); return }
      const again = findRowButton(gridBox.current, spot)
      if (again) again.focus()
    }, 0)
  }
  const reload = async () => { if (onLinksChanged) await onLinksChanged() }

  const openLink = () => { setLinkError(null); setEditing(null); setPanel(null); setLinkOpen(true); opener.current = linkButton.current }
  const closeLink = () => { setLinkOpen(false); refocus() }

  const openRule = (link, button) => { setLinkOpen(false); setPanel(null); setEditing(link); setOpener(button) }
  const closeRule = () => { setEditing(null); refocus() }

  // Who joins (the join policy) and a code link's join codes open in the
  // same edit row as the rule, one panel at a time.
  const panelOpen = (kind, link) => panel && panel.kind === kind && panel.organizationID === link.organizationID
  const openPanel = (kind, link, button) => { setLinkOpen(false); setEditing(null); setPanel({ kind, organizationID: link.organizationID }); setOpener(button) }
  const closePanel = () => { setPanel(null); refocus() }
  const handlePolicySaved = async (link, policy) => {
    setPanel(policy === 'code' ? { kind: 'codes', organizationID: link.organizationID } : null)
    await reload()
    announce(`Saved who joins ${link.organizationName}.`)
    if (policy !== 'code') refocus()
  }

  const handleLink = async org => {
    setLinkingID(org.id)
    setLinkError(null)
    try {
      await api.post({ providerID: Number(providerID), organizationID: org.id })
    } catch (exp) {
      setLinkError(serverMessage(exp, `Could not link ${org.name}.`), exp)
      setLinkingID(null)
      return
    }
    setLinkingID(null)
    setLinkOpen(false)
    await reload()
    flash(org.id)
    announce(`Linked ${org.name}.`)
    // Straight on to who joins it, starting from "Every sign-in"; closing
    // the editor returns focus to Link organization.
    opener.current = linkButton.current
    setEditing({ organizationID: org.id, organizationName: org.name, matchData: null })
  }

  const handleRuleSaved = async () => {
    const name = editing.organizationName
    setEditing(null)
    await reload()
    announce(`Rule saved for ${name}.`)
    refocus()
  }

  const askUnlink = (link, button) => { setUnlinkError(null); setUnlinking(link); setOpener(button) }
  const cancelUnlink = () => { setUnlinking(null); refocus() }
  const handleUnlink = async () => {
    const link = unlinking
    setUnlinkBusy(true)
    setUnlinkError(null)
    try {
      await api.delete(`${providerID}/${link.organizationID}`)
    } catch (exp) {
      setUnlinkError(serverMessage(exp, `Could not unlink ${link.organizationName}.`), exp)
      setUnlinkBusy(false)
      return
    }
    setUnlinkBusy(false)
    setUnlinking(null)
    if (editing && editing.organizationID === link.organizationID) setEditing(null)
    if (panel && panel.organizationID === link.organizationID) setPanel(null)
    await reload()
    announce(`Unlinked ${link.organizationName}.`)
    // The row (and its Unlink button) is gone: focus goes to the heading's button.
    opener.current = linkButton.current
    refocus()
  }

  const ruleEditor = link =>
    <OrgRuleEditor key={link.organizationID} link={link} providerID={providerID} claims={claims || []} domainNames={domains ? domains.map(d => d.domain) : null} onSaved={handleRuleSaved} onCancel={closeRule} />
  const editingListed = editing && (links || []).some(l => l.organizationID === editing.organizationID)
  // The linked organization an editor or panel below the grid is open for.
  const openID = editing && editingListed ? editing.organizationID : panel ? panel.organizationID : null
  const openListed = openID === null ? null : (links || []).find(l => l.organizationID === openID) || null
  // The rule editor edits the link as it was opened (a just-linked one
  // starts on "Every sign-in"); the panels read the current row.
  const openRow = openListed && editing && editingListed ? editing : openListed

  // The grid's row menu may hold handlers from an earlier render, so it
  // calls the current ones through this ref, and they read the current
  // state: a second click on the same action closes what the first opened.
  const actions = useRef({})
  actions.current = {
    togglePanel: (kind, link, button) => (panelOpen(kind, link) ? closePanel() : openPanel(kind, link, button)),
    toggleRule: (link, button) => (editing && editing.organizationID === link.organizationID ? closeRule() : openRule(link, button)),
    askUnlink
  }

  // Highlighted rows: the organization a claim chip pointed at, and a
  // just-linked one. AG Grid reads getRowClass as it draws a row, so a
  // change re-draws them.
  const highlightRef = useRef({})
  const rowClass = useCallback(params => {
    const { rule, flash } = highlightRef.current
    return params.data && ((rule && params.data.organizationName === rule) || (flash !== null && flash !== undefined && params.data.organizationID === flash)) ? 'sw-hl' : ''
  }, [])
  const handleGridReady = agGrid => { gridApi.current = agGrid && agGrid.api }
  useEffect(() => {
    highlightRef.current = { rule: highlightRule, flash: flashID }
    const grid = gridApi.current
    if (grid && !grid.isDestroyed()) grid.redrawRows()
  }, [highlightRule, flashID])

  const rows = useMemo(() => (Array.isArray(links) ? links.map(l => ({ ...l, id: l.organizationID, joinsWhen: joinsWhenText(l) })) : links), [links])
  const gridList = useGridList(rows)

  return (
    <div className="d-grid gap-3">
      {staff && <div style={{ maxWidth: 420 }}><FormNewOrganizationSelect /></div>}

      <WorkspaceDomains connectionName={connectionName} providerID={providerID} domains={domains} providerActive={providerActive} isPublic={providerPublic}
        organizationIDs={[ownerOrganizationID, ...(links || []).map(l => l.organizationID)]} canEdit={canEdit && staff}
        onDomainsChanged={onDomainsChanged} onProviderChanged={onProviderChanged} onAnnounce={announce} />

      <div className="sw-subh">
        <h3>Organizations people join</h3>
        <p role="status" aria-live="polite" className="sw-status">{status}</p>
        {canEdit &&
          <button type="button" ref={linkButton} className="btn btn-sm sw-btn" aria-expanded={linkOpen} aria-controls={linkOpen ? 'sw-link-panel' : undefined}
            onClick={() => (linkOpen ? closeLink() : openLink())}>Link organization</button>}
      </div>
      {linkOpen &&
        <OrgLinkPanel linkedIDs={(links || []).map(l => l.organizationID)}
          linkingID={linkingID} error={linkError} failure={linkFailure} onLink={handleLink} onClose={closeLink} />}
      {links === undefined && <p className="sw-hint m-0">Loading organizations…</p>}
      {links === null && <p className="sw-hint m-0">Organizations couldn't be loaded.</p>}
      {links &&
        <div ref={gridBox}>
          <FleetGrid tableRef={tableRef} exportName="linked-organizations" compact flush title="organizations people join">
            <TableAgClient
              ref={tableRef}
              queryKey="orgs"
              apiList={gridList}
              onReady={handleGridReady}
              getRowClass={rowClass}
              emptyState={{ Icon: IconOrganization, title: 'No organizations linked yet', message: 'Link an organization so people signing in through this connection join it.' }}>
              <ColumnText field="organizationName" header="Organization" sortable />
              <ColumnFormat field="joinsWhen" header="Joins when" render={joinsWhenCell} sortable />
              {(canEdit || canUnlink || (links || []).some(l => linkJoinPolicy(l) === 'code')) &&
                <ColumnMenu
                  menu={l => {
                    const ruled = Boolean(l.matchData && l.matchData.claim)
                    return (
                      <Menu small sentenceCase>
                        <MenuItem Icon={IconUsers} text="who joins" pin show={canEdit} onClick={e => actions.current.togglePanel('policy', l, e.currentTarget)} />
                        <MenuItem Icon={IconLoginPin} text="join codes" pin show={linkJoinPolicy(l) === 'code'} onClick={e => actions.current.togglePanel('codes', l, e.currentTarget)} />
                        <MenuItem Icon={ruled ? IconEdit : IconAdd} text={ruled ? 'edit rule' : 'add rule'} pin show={canEdit} onClick={e => actions.current.toggleRule(l, e.currentTarget)} />
                        <MenuItem Icon={IconDelete} text="unlink" pin show={canUnlink} onClick={e => actions.current.askUnlink(l, e.currentTarget)} />
                      </Menu>
                    )
                  }}
                />}
            </TableAgClient>
          </FleetGrid>
        </div>}
      {/* The open rule editor, who-joins editor or join codes of one linked
          organization, under the grid (a grid row can't hold a form). Each
          names the organization it is for. */}
      {openRow && editing && <div className="sw-edit-row" data-testid="sw-edit-row">{ruleEditor(openRow)}</div>}
      {openRow && panel && panel.kind === 'policy' &&
        <div className="sw-edit-row" data-testid="sw-policy-row">
          <JoinPolicyEditor link={openRow} providerID={providerID} onSaved={p => handlePolicySaved(openRow, p)} onCancel={closePanel} />
        </div>}
      {openRow && panel && panel.kind === 'codes' &&
        <div className="sw-edit-row" data-testid="sw-codes-row">
          <JoinCodesPanel providerID={providerID} organizationID={openRow.organizationID} organizationName={openRow.organizationName} canEdit={canEdit} />
        </div>}
      {/* A just-linked organization the refreshed list doesn't show (yet). */}
      {editing && !editingListed && ruleEditor(editing)}
      <ConfirmDialog open={Boolean(unlinking)} busy={unlinkBusy} error={unlinkError} failure={unlinkFailure}
        title={unlinking ? `Unlink ${unlinking.organizationName}?` : ''}
        body={unlinking ? `People signing in through this connection stop joining ${unlinking.organizationName}. Existing members keep their membership.` : ''}
        confirmText="Unlink" onConfirm={handleUnlink} onCancel={cancelUnlink} />
    </div>
  )
}
